import { Outlet, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { ApiRequestError } from '@/lib/api';
import { useMe } from '@/lib/queries';
import { TransactionComposerProvider } from '@/components/TransactionComposer';
import { Skeleton } from '@/components/ui/primitives';

export const Route = createFileRoute('/app')({ component: AuthenticatedLayout });

/**
 * Auth gate for everything under /app.
 *
 * Guarding on the client only - the API rejects unauthenticated requests
 * regardless, so this is a routing convenience, not the security boundary.
 * Treating it as the boundary is how "hidden" admin screens end up shipping
 * their data to anyone who knows the URL.
 */
function AuthenticatedLayout() {
  const navigate = useNavigate();
  const { data: me, isLoading, error } = useMe();

  useEffect(() => {
    if (error instanceof ApiRequestError && error.isUnauthorized) {
      void navigate({ to: '/login', replace: true });
    }
  }, [error, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-dvh bg-[var(--bg)] p-5">
        <Skeleton className="h-9 w-40" />
        <Skeleton className="mt-6 h-40 w-full" />
        <Skeleton className="mt-4 h-64 w-full" />
      </div>
    );
  }

  if (!me) return null;

  return (
    <TransactionComposerProvider>
      <Outlet />
    </TransactionComposerProvider>
  );
}
